import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import MapAreaSelector from '../MapAreaSelector/Main';
import { calculateArea } from '../../services/areaCalculation';

function Main() {
  const navigate = useNavigate();
  const [showMap, setShowMap] = useState(false);
  const [mapCenter, setMapCenter] = useState(null);
  const [polygonCoordinates, setPolygonCoordinates] = useState(null);
  const [calculatedArea, setCalculatedArea] = useState(null);
  const [areas, setAreas] = useState([]); // Array of {coordinates, area}
  const [totalArea, setTotalArea] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [zipCode, setZipCode] = useState('');

  const handleZipCodeSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      // Use Nominatim API for geocoding
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(zipCode)}&limit=1`,
        {
          headers: {
            'User-Agent': 'SolarEnergyApp/1.0',
          },
        }
      );

      const data = await response.json();

      if (data && data.length > 0) {
        const location = {
          lat: parseFloat(data[0].lat),
          lng: parseFloat(data[0].lon),
        };
        setMapCenter(location);
        setShowMap(true);
      } else {
        setError('لم يتم العثور على الموقع. يرجى المحاولة مرة أخرى.');
      }
    } catch (err) {
      console.error('Geocoding error:', err);
      setError('حدث خطأ أثناء البحث عن الموقع. يرجى المحاولة مرة أخرى.');
    } finally {
      setLoading(false);
    }
  };

  const handleMapIconClick = () => {
    setError(null);
    if (navigator.geolocation) {
      setLoading(true);
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const location = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          };
          setMapCenter(location);
          setShowMap(true);
          setLoading(false);
        },
        (err) => {
          console.error('Geolocation error:', err);
          // Default to a location (e.g., Jordan)
          setMapCenter({ lat: 31.9522, lng: 35.2332 });
          setShowMap(true);
          setLoading(false);
        },
        {
          enableHighAccuracy: false,
          timeout: 15000,
          maximumAge: 60000,
        }
      );
    } else {
      // Browser doesn't support geolocation, use default location
      setMapCenter({ lat: 31.9522, lng: 35.2332 });
      setShowMap(true);
    }
  };

  const handleAreaSelected = async (coordinates) => {
    console.log('handleAreaSelected called with coordinates:', coordinates);
    console.log('Number of coordinates:', coordinates ? coordinates.length : 0);
    
    if (!coordinates || coordinates.length !== 4) {
      console.error('Invalid coordinates received:', coordinates);
      setError('يرجى تحديد 4 نقاط بشكل صحيح');
      return;
    }
    
    setLoading(true);
    setError(null);

    try {
      const area = await calculateArea(coordinates);
      console.log('Calculated area:', area);
      
      // Add new area to the list and calculate total
      const newArea = { coordinates, area };
      setAreas(prevAreas => {
        const updatedAreas = [...prevAreas, newArea];
        // Calculate total area from updated list
        const newTotal = updatedAreas.reduce((sum, a) => sum + a.area, 0);
        setTotalArea(newTotal);
        return updatedAreas;
      });
      
      // Keep for backward compatibility
      setPolygonCoordinates(coordinates);
      setCalculatedArea(area);
    } catch (err) {
      console.error('Area calculation error:', err);
      setError('حدث خطأ أثناء حساب المساحة. يرجى المحاولة مرة أخرى.');
    } finally {
      setLoading(false);
    }
  };

  const handleProceedToCustomerInfo = () => {
    if (totalArea > 0 && areas.length > 0) {
      // Combine all coordinates from all areas
      const allCoordinates = areas.flatMap(a => a.coordinates);
      navigate('/customer-info', {
        state: {
          area: totalArea, // Total area
          coordinates: allCoordinates, // All coordinates combined
          areas: areas, // Individual areas for reference
          location: mapCenter,
        },
      });
    } else if (calculatedArea && polygonCoordinates) {
      // Fallback for single area
      navigate('/customer-info', {
        state: {
          area: calculatedArea,
          coordinates: polygonCoordinates,
          location: mapCenter,
        },
      });
    }
  };

  if (showMap) {
    return (
      <main>
        <section className="map-area pt-120 pb-120">
          <div className="container">
            <div className="row">
              <div className="col-lg-12">
                <div className="section-title text-center mb-50">
                  <h5>AREA SELECTION</h5>
                  <h2>Select Your Area</h2>
                  <p>Click on the map to draw a rectangle using 4 points</p>
                </div>

                {error && (
                  <div className="alert alert-danger" role="alert" style={{ marginBottom: '20px' }}>
                    {error}
                  </div>
                )}

                {loading && (
                  <div className="text-center mb-20">
                    <p>جاري الحساب...</p>
                  </div>
                )}

                <MapAreaSelector
                  center={mapCenter}
                  onAreaSelected={handleAreaSelected}
                  initialZoom={20}
                />

                {(totalArea > 0 || calculatedArea) && (
                  <div className="text-center mt-30">
                    <div
                      style={{
                        backgroundColor: '#f8f9fa',
                        padding: '20px',
                        borderRadius: '8px',
                        marginBottom: '20px',
                        display: 'inline-block',
                      }}
                    >
                      {areas.length > 1 ? (
                        <>
                          <h4>عدد المساحات المحددة: {areas.length}</h4>
                          <h4 style={{ marginTop: '10px' }}>
                            المساحة الإجمالية: {totalArea.toFixed(2)} متر مربع
                          </h4>
                          <div style={{ marginTop: '10px', fontSize: '14px', color: '#666' }}>
                            {areas.map((area, index) => (
                              <div key={index}>
                                المساحة {index + 1}: {area.area.toFixed(2)} م²
                              </div>
                            ))}
                          </div>
                        </>
                      ) : (
                        <h4>المساحة المحسوبة: {(totalArea || calculatedArea || 0).toFixed(2)} متر مربع</h4>
                      )}
                    </div>
                    <div style={{ marginTop: '10px' }}>
                      <button
                        onClick={handleProceedToCustomerInfo}
                        className="btn ss-btn active"
                        style={{ fontSize: '18px', padding: '12px 30px', marginRight: '10px' }}
                      >
                        المتابعة إلى معلومات العميل
                      </button>
                      {areas.length > 0 && (
                        <button
                          onClick={() => {
                            setAreas([]);
                            setTotalArea(0);
                            setCalculatedArea(null);
                            setPolygonCoordinates(null);
                          }}
                          className="btn ss-btn"
                          style={{ 
                            fontSize: '18px', 
                            padding: '12px 30px',
                            backgroundColor: '#6c757d',
                            border: 'none'
                          }}
                        >
                          مسح جميع المساحات
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>
      </main>
    );
  }

  return (
    <main>
      <section className="location-entry-area pt-120 pb-120">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-8">
              <div className="section-title text-center mb-50">
                <h5>LOCATION ENTRY</h5>
                <h2>Select Your Location</h2>
                <p>Choose your location using one of the options below</p>
              </div>

              {error && (
                <div className="alert alert-danger" role="alert" style={{ marginBottom: '20px' }}>
                  {error}
                </div>
              )}

              <div className="row">
                <div className="col-lg-6 mb-30">
                  <div
                    className="location-option-card"
                    style={{
                      padding: '40px',
                      border: '2px solid #dee2e6',
                      borderRadius: '8px',
                      textAlign: 'center',
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                    }}
                  >
                    <i
                      className="fas fa-map-marker-alt"
                      style={{ fontSize: '48px', color: '#4CAF50', marginBottom: '20px' }}
                    />
                    <h4 style={{ marginBottom: '20px' }}>Enter Zip Code</h4>
                    <form onSubmit={handleZipCodeSubmit}>
                      <div className="contact-field p-relative c-name mb-20">
                        <input
                          type="text"
                          placeholder="Enter Zip Code"
                          value={zipCode}
                          onChange={(e) => setZipCode(e.target.value)}
                          required
                          style={{
                            width: '100%',
                            padding: '12px 15px',
                            border: '1px solid #dee2e6',
                            borderRadius: '4px',
                          }}
                        />
                      </div>
                      <button
                        type="submit"
                        className="btn ss-btn active"
                        disabled={loading}
                        style={{ width: '100%' }}
                      >
                        {loading ? 'جاري البحث...' : 'فتح الخريطة'}
                      </button>
                    </form>
                  </div>
                </div>

                <div className="col-lg-6 mb-30">
                  <div
                    className="location-option-card"
                    style={{
                      padding: '40px',
                      border: '2px solid #dee2e6',
                      borderRadius: '8px',
                      textAlign: 'center',
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      cursor: 'pointer',
                    }}
                    onClick={handleMapIconClick}
                  >
                    <i
                      className="fas fa-map"
                      style={{ fontSize: '48px', color: '#4CAF50', marginBottom: '20px' }}
                    />
                    <h4 style={{ marginBottom: '20px' }}>Use Current Location</h4>
                    <p style={{ color: '#6c757d' }}>
                      Click to open the map at your current location
                    </p>
                    {loading && <p>جاري تحديد الموقع...</p>}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}

export default Main;
